// JQ4-29 — US-TICKETS-009: Supervisor real-time operations dashboard API client wrappers.

import type {
  SupervisorActiveTicketsResponseDTO,
  SupervisorShiftMetricsDTO,
} from '@jq4race/shared';
import {
  supervisorActiveTicketsResponseSchema,
  supervisorShiftMetricsSchema,
} from '@jq4race/shared';

import { request } from './client.js';

/**
 * getSupervisorActiveTickets — GET /supervisor/locations/:locationId/active-tickets
 *
 * Returns all active tickets (parked | requested | in_transit) for the location,
 * sorted by status priority (requested first, then in_transit, then parked).
 *
 * Requires supervisor, org_admin, or super_admin role.
 * Throws ApiError(404) when location not found or cross-org request.
 * Throws ApiError(403) when the caller lacks supervisor-level access.
 */
export async function getSupervisorActiveTickets(
  locationId: string,
): Promise<SupervisorActiveTicketsResponseDTO> {
  const payload = await request<unknown>(
    `/supervisor/locations/${encodeURIComponent(locationId)}/active-tickets`,
    { method: 'GET' },
  );
  return supervisorActiveTicketsResponseSchema.parse(payload);
}

/**
 * getSupervisorShiftMetrics — GET /supervisor/locations/:locationId/shift-metrics
 *
 * Returns shift KPI metrics for the supervisor floor view, scoped to today's local-day window.
 *
 * Requires supervisor, org_admin, or super_admin role.
 * Throws ApiError(404) when location not found or cross-org request.
 * Throws ApiError(403) when the caller lacks supervisor-level access.
 */
export async function getSupervisorShiftMetrics(
  locationId: string,
): Promise<SupervisorShiftMetricsDTO> {
  const payload = await request<unknown>(
    `/supervisor/locations/${encodeURIComponent(locationId)}/shift-metrics`,
    { method: 'GET' },
  );
  return supervisorShiftMetricsSchema.parse(payload);
}
